import React, { useState, useEffect, useCallback, useLayoutEffect } from "react";

import { useRecoilValue, useSetRecoilState } from "recoil";
import { routeInfo, userCredentials } from "recoilState/GlobalState";

import XGrid from "components/UI/Grid/XGrid";
import { HeaderButton, Buttons } from "components";
import Modal from "components/UI/Modal/Modal";
import { useService, puedeEditarServicio } from "components/route/ServiceRoute";
import Add from "@mui/icons-material/Add";
import Cached from "@mui/icons-material/Cached";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import EditOutlined from "@mui/icons-material/EditOutlined";
import ScheduleOutlined from "@mui/icons-material/ScheduleOutlined";

import { DeleteModal, InsertModal, UpdateModal, RangoModal } from "./Actions";
import CeldaDeChips from "components/UI/Grid/CeldaDeChips";
import { RNHHorarioGet } from "services/services-mia/programa-salud/personal/RNHHorarioGet";

import { horariosTitles } from "consts/titulos-de-tablas";
import { ACTIONS } from "consts/Actions";

import s from "styles/pages/Pages.module.scss";

// Acción propia de esta vista: no es un alta/baja/modificación del horario sino
// entrar a sus franjas. Va como string suelto porque ACTIONS solo tiene el ABM.
const VER_RANGOS = "RANGOS";

// El renderCell se inyecta acá y no en `titulos-de-tablas` para que ese archivo
// siga siendo solo datos, sin importar componentes. Es una constante de módulo:
// recalcularlo en cada render le cambiaría la identidad a las columnas al grid.
const titles = horariosTitles.map((t) =>
    t.field === "rangos"
        ? {
            ...t,
            renderCell: ({ value }) => (
                <CeldaDeChips items={value} vacio="Sin rangos" />
            ),
        }
        : t
);

/**
 * "08:00:00.000000" -> "08:00".
 *
 * Del back llegan con segundos y microsegundos. En una celda angosta sobra todo menos
 * la hora y el minuto, y cortar por posicion alcanza porque el formato es fijo.
 */
const soloHoraYMinutos = (hora) => String(hora ?? "").substring(0, 5);

/**
 * Horarios: el catálogo de jornadas. Cada horario tiene adentro sus RANGOS, que son
 * las franjas concretas (entrada, salida y días), y se gestionan desde el botón
 * "Rangos horarios", que abre la vista de rangos dentro de un modal.
 *
 * La columna de rangos se llena con lo que el Get ya trae anidado en cada horario
 * (`rnhHorarioRangos`), así que no hace falta pedir las franjas aparte. Ojo: ahí vienen
 * SOLO la entrada y la salida —sin id, sin días—, lo justo para la celda. El modal de
 * franjas pide las suyas completas con `RNHHorarioRangoGet`.
 */
export default function Horarios() {
    const [isLoading, setIsLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [modalDetails, setModalDetails] = useState({ isOpen: false });
    const [rowSelected, setRowSelected] = useState({ status: false, row: {} });

    const { service } = useService();
    const puedeEscribir = puedeEditarServicio(service);
    const setRoute = useSetRecoilState(routeInfo);
    const credentials = useRecoilValue(userCredentials);

    const updateList = useCallback(async () => {
        setRows([]);
        setRowSelected({ status: false, row: {} });
        setIsLoading(true);

        const respuesta = await RNHHorarioGet(credentials);
        setIsLoading(false);
        if (!respuesta.ok) return;

        setRows(
            respuesta.data
                // Fila centinela: sin registros el back manda una con todo en null.
                .filter((d) => d?.rnhHorarioId)
                .map((d) => ({
                    id: d.rnhHorarioId,
                    cod: d.rnhHorarioCod ?? "",
                    nombre: d.rnhHorarioNombre ?? "",
                    descripcion: d.rnhHorarioDescripcion ?? "",
                    // La version no es columna: la piden el Upd y el Del.
                    version: d.rnhHorarioVersion,
                    // Anidados en la response. Se arman las ETIQUETAS aca y no en la
                    // celda: `CeldaDeChips` es generica y recibe strings, asi que el
                    // formato —como el resto del mapeo de la response— es del loader.
                    rangos: (d.rnhHorarioRangos ?? []).map(
                        (r) =>
                            `${soloHoraYMinutos(r.rnhHorarioRangoEntrada)} - ${soloHoraYMinutos(
                                r.rnhHorarioRangoSalida
                            )}`
                    ),
                }))
        );
    }, [credentials]);

    useLayoutEffect(() => {
        setRoute({ wordKey: "Horarios - Programa Salud RN" });
    }, [setRoute, service.servicioNombre]);

    useEffect(() => {
        updateList();
    }, [service, updateList]);

    const handleClick = ({ procede, title }) =>
        setModalDetails({ procede, title, isOpen: true });

    /**
     * El modal de rangos escribe en OTRA tabla, y la columna de chips de esta grilla
     * se alimenta de ella. Se marca el cambio y se refresca al CERRAR, no en cada
     * operacion: refrescar con el modal abierto le cambia la fila elegida abajo.
     */
    const [huboCambios, setHuboCambios] = useState(false);

    const closeModal = () => {
        setModalDetails({ isOpen: false });
        if (huboCambios) {
            setHuboCambios(false);
            updateList();
        }
    };

    // Una fila elegida habilita lo que opera SOBRE ella; ninguna habilita el alta.
    const hayElegido = Boolean(rowSelected.row?.id);

    return (
        <main className={s.main}>
            {/* Tres tallas, una por tipo de contenido: la baja son dos campos
                deshabilitados, el alta y la edición un formulario de una columna, y
                los rangos una grilla.
                `lg` y no `xl` para los rangos: embebida la grilla no lleva la columna
                de horario, así que sus nueve columnas suman unos 650px. En 1200px
                sobraban 500 de aire. */}
            <Modal
                size={
                    modalDetails.procede === VER_RANGOS
                        ? "lg"
                        : modalDetails.procede === ACTIONS.DELETE
                            ? "sm"
                            : "md"
                }
                onClose={closeModal}
                open={modalDetails.isOpen}
                title={modalDetails.title}
            >
                {modalDetails.procede === ACTIONS.INSERT && (
                    <InsertModal updateList={updateList} closeModal={closeModal} />
                )}
                {modalDetails.procede === ACTIONS.UPDATE && (
                    <UpdateModal
                        horario={rowSelected.row}
                        updateList={updateList}
                        closeModal={closeModal}
                    />
                )}
                {modalDetails.procede === ACTIONS.DELETE && (
                    <DeleteModal
                        horario={rowSelected.row}
                        updateList={updateList}
                        closeModal={closeModal}
                    />
                )}
                {modalDetails.procede === VER_RANGOS && (
                    <RangoModal
                        horario={rowSelected.row}
                        onCambio={() => setHuboCambios(true)}
                        closeModal={closeModal}
                    />
                )}
            </Modal>

            <div className={s.actions}>
                <Buttons>
                    <HeaderButton text={"Actualizar"} onClick={updateList}>
                        <Cached />
                    </HeaderButton>
                    <HeaderButton
                        text={"Rangos Horarios"}
                        handleClick={handleClick}
                        data={{
                            title: `Rangos Horarios${rowSelected.row?.nombre ? ` · ${rowSelected.row.nombre}` : ""}`,
                            procede: VER_RANGOS,
                        }}
                        // Mirar los rangos no es escribir: un Visualizador entra.
                        disabled={!hayElegido}
                    >
                        <ScheduleOutlined />
                    </HeaderButton>
                    <HeaderButton
                        text={"Agregar Horario"}
                        handleClick={handleClick}
                        data={{ title: "Agregar Horario", procede: ACTIONS.INSERT }}
                        disabled={hayElegido || !puedeEscribir}
                    >
                        <Add />
                    </HeaderButton>
                    <HeaderButton
                        text={"Editar Horario"}
                        handleClick={handleClick}
                        data={{ title: "Editar Horario", procede: ACTIONS.UPDATE }}
                        disabled={!hayElegido || !puedeEscribir}
                    >
                        <EditOutlined />
                    </HeaderButton>
                    <HeaderButton
                        text={"Eliminar Horario"}
                        handleClick={handleClick}
                        data={{ title: "Eliminar Horario", procede: ACTIONS.DELETE }}
                        disabled={!hayElegido || !puedeEscribir}
                    >
                        <DeleteOutline />
                    </HeaderButton>
                </Buttons>
            </div>

            <div className={s.container}>
                <XGrid
                    rows={rows}
                    titles={titles}
                    loading={isLoading}
                    secondarySelected={rowSelected}
                    setSecondarySelected={setRowSelected}
                />
            </div>
        </main>
    );
}
